import React from "react";
import { useTranslation } from "react-i18next";

import "../../styles/theme.css";
import "../../styles/dashboard.css";
import "../../styles/ProfilesManager.css";
import { useSidebar } from "../../App";

export default function HelpProfile() {
  const { sidebarCollapsed } = useSidebar();
  const { t } = useTranslation("help");

  const rowsData = t("helpProfile.table.rows", {
    returnObjects: true,
    defaultValue: {
      profileName: {
        field: "Profile Name",
        whatItMeans: "The display name for the reusable test plan.",
        howToFillIt:
          "Use a name that combines endpoint, test family, and purpose, such as ClaimsBot-PromptSafety-QA or SecureMCP-SafeMode-Baseline."
      },
      profileDescription: {
        field: "Profile Description",
        whatItMeans: "A note describing what the profile is intended to validate.",
        howToFillIt:
          "Mention environment, coverage goal, selected datasets, and any expected scan constraints."
      },
      endpoint: {
        field: "Endpoint",
        whatItMeans: "The saved endpoint that this profile will test.",
        howToFillIt:
          "Select the endpoint created earlier. The profile type should match the endpoint family whenever possible."
      },
      profileType: {
        field: "Profile Type",
        whatItMeans: "The attack-surface family to test.",
        howToFillIt:
          "Choose Prompts & Docs, AI-specific Denial-of-Service, AI Model Attack Surface, MCP Attack Surface, Response Time Optimization, or Regulation Compliance."
      },
      parametersScript: {
        field: "Parameters / Script",
        whatItMeans: "How WhiteHaX should configure the test execution.",
        howToFillIt:
          "Use Parameters for built-in UI settings. Use Script when the test needs custom Python logic or a custom API wrapper."
      },
      testFiles: {
        field: "Test Files",
        whatItMeans:
          "The datasets or files that supply prompts, documents, or cases.",
        howToFillIt:
          "Move built-in or uploaded datasets into Selected before finishing. Only selected files will be included in the profile run."
      }
    }
  });

  const tableRows = Array.isArray(rowsData)
    ? rowsData
    : Object.values(rowsData || {});

  return (
    <div className="theme-whbl">
      <style>
        {`
.dark-guide-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: "Segoe UI", Arial, sans-serif;
  font-size: 14px;
  color: #e5e7eb;
  background: #111827;
  border: 1px solid #374151;
}

.dark-guide-table thead th {
  background: #1f2937;
  color: #60a5fa;
  font-weight: 700;
  text-align: left;
  padding: 12px 14px;
  border: 1px solid #4b5563;
  vertical-align: middle;
}

.dark-guide-table tbody td {
  background: #111827;
  color: #e5e7eb;
  padding: 12px 14px;
  border: 1px solid #374151;
  vertical-align: top;
  line-height: 1.55;
}

.dark-guide-table tbody tr:nth-child(even) td {
  background: #172033;
}

.dark-guide-table tbody tr:hover td {
  background: #1e293b;
}

.dark-guide-table th:nth-child(1),
.dark-guide-table td:nth-child(1) {
  width: 23%;
}

.dark-guide-table th:nth-child(2),
.dark-guide-table td:nth-child(2) {
  width: 30%;
}

.dark-guide-table th:nth-child(3),
.dark-guide-table td:nth-child(3) {
  width: 47%;
}

@media (max-width: 768px) {
  .dark-guide-table {
    min-width: 750px;
  }
}
`}
      </style>

      {/* Application Header */}

      <div className="container-fluid">
        <div className="row">
          <main
            className={`${sidebarCollapsed ? "col-11" : "col-10"}`}
            style={{
              width: "100%",
              overflowX: "auto",
            }}
          >
            <div className="dashboard">
              {/* ===== Page Header ===== */}
              <div className="pm-header">
                <div className="pm-title-section">
                  <h1 className="pm-title">
                    {t("helpProfile.title", "Create Profile")}
                  </h1>
                </div>
              </div>

              {/* ===== Help Content ===== */}

              {/* Step 1 */}
              <div>
                <p>
                  {t(
                    "helpProfile.description",
                    "A profile is the reusable test plan WhiteHaX runs against a saved endpoint. The profile selects the attack-surface type, chooses built-in or uploaded test files, and sets the parameters or scripts that control how the scan is executed."
                  )}
                </p>
                <p style={{ border: "1px solid #fff", padding: "5px" }}>
                  {t(
                    "helpProfile.plainEnglishMeaning",
                    "Plain-English meaning: The endpoint answers the question 'what system are we testing?' The profile answers 'which tests do we run, with what inputs, and under what configuration?'"
                  )}
                </p>
              </div>

              {/* MCP Tester */}
              <div>
                <img
                  className="help-img"
                  src="/images/mcpj1.jpg"
                  alt="MCP Tester"
                />

                <h2 className="help-section-title">
                  {t("helpProfile.sectionTitle", "Common Profile Fields")}
                </h2>

                <table className="dark-guide-table">
                  <thead>
                    <tr>
                      <th>
                        {t("helpProfile.table.headers.field", "Field")}
                      </th>
                      <th>
                        {t(
                          "helpProfile.table.headers.whatItMeans",
                          "What it means"
                        )}
                      </th>
                      <th>
                        {t(
                          "helpProfile.table.headers.howToFillIt",
                          "How to fill it"
                        )}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {tableRows.map((row, index) => (
                      <tr key={index}>
                        <td>{row.field}</td>
                        <td>{row.whatItMeans}</td>
                        <td>{row.howToFillIt}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}